import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, Container, Modal, Row } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext, useToast, useTranslator } from '@front-activerse/common-components';
import { useCameraCapture } from '../hooks';
import { AddUserProductFormValues } from '../AddUserProduct/helpers';
import {
  setPendingScanLabelPhotos,
  type ScanLabelAnalyzingState,
} from './TrackerScanLabelAnalyzingContainer';

const INTRO_SEEN_KEY = 'tracker_scan_label_intro_seen';

type ScanStep = 'front' | 'nutrition';

const isScannerPath = (pathname: string) => pathname.endsWith('/scan-label');

interface TrackerScanLabelContainerProps {
  cancelScanLabel: () => void;
}

const TrackerScanLabelContainer: React.FC<TrackerScanLabelContainerProps> = ({
  cancelScanLabel,
}) => {
  const { t, language } = useTranslator();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as {
    formValues?: AddUserProductFormValues;
    fromAutoScan?: boolean;
    fromUserMeals?: boolean;
  } | null;
  const formValues = locationState?.formValues;
  // When launched straight from the action sheet (autoScan), an empty add-product form sits
  // between the tracker and this scanner. Closing should skip it and return to the tracker; when
  // opened from an actual add-product form, closing returns to that form.
  const fromAutoScan = locationState?.fromAutoScan ?? false;
  // Entered from the "Your meals" sheet rather than the tracker's: the product still gets its own
  // new meal, but that meal belongs to the user-meals base ("save to my meals"), not the daily log.
  const fromUserMeals = locationState?.fromUserMeals ?? false;
  const { toastError } = useToast();

  const {
    images: {
      cameraShutter,
      closeWhiteIcon,
      flashOnIcon,
      flashOffIcon,
      photoGalleryIcon,
      videoPoster,
      scanLabelTipsImg,
      scanLabelTipsImgPl,
      scanLabelTipsImgSv,
      scanLabelTipsImgPt,
      scanLabelTipsImgCz,
      scanInfoIcon,
      doneIcon,
    },
  } = useAppContext();

  const camera = useCameraCapture();
  const [step, setStep] = useState<ScanStep>('front');
  // Intro bottom sheet ("Take 2 photos"): shown automatically on the first ever visit and
  // re-openable any time via the "i" button — same content in both cases.
  const [showIntro, setShowIntro] = useState(() => !localStorage.getItem(INTRO_SEEN_KEY));
  const [frontThumbUrl, setFrontThumbUrl] = useState<string | null>(null);
  const frontPhotoRef = useRef<File | null>(null);
  const scanAreaRef = useRef<HTMLDivElement | null>(null);
  // Mirror of the thumb URL so the route-cleanup below can revoke whatever is current without
  // taking it as an effect dependency (which would re-run the camera lifecycle on every shot).
  const frontThumbUrlRef = useRef<string | null>(null);
  frontThumbUrlRef.current = frontThumbUrl;

  // Camera lifecycle — tied to this being the visible route, NOT to mount/unmount:
  // SlideRoutes renders with destroy={false}, so this component stays mounted after
  // navigating away (back gesture, TitleBar back, successful scan). A mount-effect
  // cleanup would never run and the device camera would stay on.
  //
  // The page keys this component on `location.key`, which makes a NEW visit a fresh mount. Going
  // BACK, though, restores the entry this instance was already mounted for — same key, same
  // instance — so returning from the analysis route has to reset the step explicitly. Without it the
  // user landed on a stale 2/2 with the previous front photo still pinned.
  const isActiveRoute = isScannerPath(location.pathname);

  useEffect(() => {
    if (!isActiveRoute) return undefined;
    let active = true;
    const init = async () => {
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (active) await camera.startCamera();
    };
    init();
    const stop = () => camera.stopCamera();
    // While this page is kept mounted, its `useLocation()` never changes — verified by logging
    // `location.pathname`/`location.key` across a full flow: both stay pinned to the entry it first
    // rendered for, while `window.location` moves. So navigation has to come from popstate.
    const handlePopState = () => {
      if (!isScannerPath(window.location.pathname)) {
        camera.stopCamera();
        return;
      }
      // Back onto the scanner (from the analysis route) restarts at step 1/2 with the front of the
      // product — IP-11084 case 1. Without this the user landed on a stale 2/2 with the previous
      // front photo still pinned.
      if (frontThumbUrlRef.current) URL.revokeObjectURL(frontThumbUrlRef.current);
      frontPhotoRef.current = null;
      setFrontThumbUrl(null);
      setStep('front');
      camera.startCamera();
    };
    window.addEventListener('beforeunload', stop);
    window.addEventListener('pagehide', stop);
    window.addEventListener('popstate', handlePopState);
    return () => {
      active = false;
      camera.stopCamera();
      window.removeEventListener('beforeunload', stop);
      window.removeEventListener('pagehide', stop);
      window.removeEventListener('popstate', handlePopState);
      if (frontThumbUrlRef.current) URL.revokeObjectURL(frontThumbUrlRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveRoute]);

  // Granting camera access from the browser's own settings leaves the "no access" alert on screen
  // next to a live preview: the lifecycle effect above runs once per route entry, so nothing calls
  // startCamera() again and the hook's error (only cleared on startCamera) goes stale. Retry when
  // the permission flips to granted, and — because Safari does not expose 'camera' to the
  // Permissions API — also when the tab regains focus, which is when the user comes back from the
  // settings UI. The retry is a no-op unless we are actually holding an error.
  useEffect(() => {
    if (!isActiveRoute) return undefined;

    const retryIfBlocked = () => {
      if (!camera.error) return;
      camera.startCamera();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') retryIfBlocked();
    };

    window.addEventListener('focus', retryIfBlocked);
    document.addEventListener('visibilitychange', handleVisibility);

    // Chrome/Edge/Firefox report camera permission changes live, so recovery happens the moment
    // the user flips the toggle without waiting for a tab switch.
    let permissionStatus: PermissionStatus | null = null;
    const onPermissionChange = () => {
      if (permissionStatus?.state === 'granted') retryIfBlocked();
    };
    navigator.permissions
      ?.query({ name: 'camera' as PermissionName })
      .then((status) => {
        permissionStatus = status;
        status.addEventListener('change', onPermissionChange);
      })
      // Safari rejects on the unsupported 'camera' name — the listeners above still cover it.
      .catch(() => undefined);

    return () => {
      window.removeEventListener('focus', retryIfBlocked);
      document.removeEventListener('visibilitychange', handleVisibility);
      permissionStatus?.removeEventListener('change', onPermissionChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveRoute, camera.error]);

  // Blob URLs are revoked explicitly — when replaced by a new shot and when the route is left (see
  // the lifecycle effect above). Deliberately NOT in a `[thumbUrl]`-keyed cleanup: that also fires
  // whenever the component re-renders its way out of a kept-mounted state (SlideRoutes
  // destroy={false}), which revoked the very blob still sitting in an <img src> and left grey
  // placeholders behind.

  const dismissIntro = () => {
    localStorage.setItem(INTRO_SEEN_KEY, 'true');
    setShowIntro(false);
  };

  const openIntro = () => setShowIntro(true);

  // The tips image carries baked-in label text, so it has to follow the UI language. Mirrors how the
  // designers ship it: `tracker_scan_label_tips_img.png` is the brand's base artwork, and a
  // per-language file exists only where they provided one (today: `_pl` on dtba and maxx). Anything
  // without its own file falls back to the base — for the Polish-only brands (oxy, hashimoto) that
  // base IS the Polish artwork, which is what those Drive folders contain.
  //
  // A map rather than a switch: the per-language entries are optional AND brand-specific, so adding
  // e.g. a Portuguese file for radiance is one import in that brand's images.js — this shared
  // component needs no change, and the other seven brands keep falling back to their base.
  const localisedTipsImg: Record<string, string | undefined> = {
    'pl-PL': scanLabelTipsImgPl,
    'sv-SE': scanLabelTipsImgSv,
    'pt-BR': scanLabelTipsImgPt,
    'cs-CZ': scanLabelTipsImgCz,
  };
  const tipsImg = localisedTipsImg[language] ?? scanLabelTipsImg;

  // Both photos taken — hand them to the analysis route. Splitting the analysis off onto its own
  // history entry is what makes back-during-analysis return here (see the scan-plate pair
  // `/scan-plate` -> `/scan-plate/details`), instead of popping the whole scanner off the stack.
  const startAnalysis = async (front: File, nutrition: File) => {
    await camera.stopCamera();
    // The photos go through a module-level holder, NOT location.state: `File` cannot be cloned into
    // history state, so navigating back to the analysis route would restore it empty and the screen
    // would look photo-less (which sent the user out to the camera).
    setPendingScanLabelPhotos(front, nutrition);
    const analyzingState: ScanLabelAnalyzingState = {
      ...(formValues && { formValues }),
      fromAutoScan,
      fromUserMeals,
    };
    // `/analyzing` is stripped before appending it: SlideRoutes (destroy={false}) keeps this
    // component mounted with a FROZEN render location, so on a second scan `location.pathname`
    // still read `…/scan-label/analyzing` from the previous run and this produced
    // `…/analyzing/analyzing` — a route that matches nothing, which dropped the state (and with it
    // the photos) and reopened the add-product form empty.
    const scannerPath = location.pathname.replace(/\/analyzing$/, '');
    navigate(`${scannerPath}/analyzing`, { state: analyzingState });
  };

  const handleCapture = async (file: File) => {
    if (step === 'front') {
      frontPhotoRef.current = file;
      setFrontThumbUrl((previous) => {
        if (previous) URL.revokeObjectURL(previous);
        return URL.createObjectURL(file);
      });
      setStep('nutrition');
    } else if (step === 'nutrition' && frontPhotoRef.current) {
      await startAnalysis(frontPhotoRef.current, file);
    }
  };

  const handleShutter = async () => {
    if (camera.isCapturing) return;
    try {
      // Crop exactly to the white scan frame the user aims with, at full source resolution
      // (no downscaling) and high JPEG quality — the nutrition table fills the sent image,
      // which is what the model actually needs to read small print.
      const file = await camera.capturePhoto({
        crop: 'element',
        cropElement: scanAreaRef.current,
        quality: 0.95,
        fileName: step === 'front' ? 'label-front.jpg' : 'label-nutrition.jpg',
      });
      await handleCapture(file);
    } catch {
      toastError();
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Gallery uploads have no scan frame to crop to — send the whole photo, but keep it under
    // the backend's 1MB-per-image limit (1600px + q0.92 instead of the legacy 1024px + q0.9).
    const file = await camera.processUploadedFile(event, {
      crop: 'scale',
      maxDimension: 1600,
      quality: 0.92,
      fileName: step === 'front' ? 'label-front.jpg' : 'label-nutrition.jpg',
    });
    if (file) await handleCapture(file);
  };

  const handleClose = async () => {
    cancelScanLabel();
    await camera.stopCamera();
    // The scanner is entered directly (no add-product launcher in between) in BOTH flows now, so a
    // single back step is correct: autoScan came straight from the tracker, and the form flow came
    // from the add-product form. navigate(-1) also keeps the SlideRoutes slide-out animation.
    navigate(-1);
  };

  return (
    <div className="scan-plate">
      <video
        ref={camera.videoRef}
        className="camera-video-fullscreen"
        poster={videoPoster}
        playsInline
        muted
        style={{ opacity: camera.isVideoVisible ? 1 : 0 }}
      />
      <canvas ref={camera.canvasRef} className="d-none" />
      <input
        ref={camera.fileInputRef}
        type="file"
        accept="image/*"
        className="d-none"
        onChange={handleFileUpload}
      />

      <div className="camera-overlay-fullscreen">
        {camera.isCameraActive && <div className="scan-area" ref={scanAreaRef} />}

        {frontThumbUrl && step === 'nutrition' && !camera.error && (
          <div
            className="captured-photo captured-photo-anchored"
            data-testid="scan-label-front-thumb"
            data-test-id="scan_label_front_thumb"
          >
            <img src={frontThumbUrl} alt="" />
            <img src={doneIcon} alt="" className="captured-photo-badge" />
          </div>
        )}

        <div className="top-controls">
          <button
            className="scan-plate-button"
            onClick={handleClose}
            data-testid="scan-label-close"
            data-test-id="scan_label_close"
          >
            <img src={closeWhiteIcon} alt="" />
          </button>
          <p className="mb-0 scan-steps-pill">
            <span className="semi-bold">
              {t('tracker.scanner.steps.current', {
                current: step === 'front' ? 1 : 2,
                total: 2,
              })}
            </span>{' '}
            {/* Graphic separator, not copy — kept out of the locale files for the same reason the
                tick mark was: there is nothing to translate. */}
            ·{' '}
            <span>
              {step === 'front'
                ? t('tracker.scanner.steps.frontOfProduct')
                : t('tracker.scanner.steps.nutritionLabel')}
            </span>
          </p>
          <button
            className="scan-plate-button"
            onClick={openIntro}
            data-testid="scan-label-info"
            data-test-id="scan_label_info"
            aria-label={t('tracker.scanner.intro.title')}
          >
            <img src={scanInfoIcon} alt="" />
          </button>
        </div>

        <div className="bottom-controls">
          {/* Analysis failures are not handled here: they always carry step === 'analyzing', so the
              earlier return renders them on the analyzing screen with the photos still visible. */}
          {camera.error ? (
            <div className="text-center w-100">
              <Alert color="danger" className="mb-0" fade={false}>
                <p className="mb-0 text-text-main-color text-start">
                  {t('tracker.scanFood.noAccessCamera.notification')}
                </p>
              </Alert>
              {/* No gallery button here on purpose: without camera permission the browser file
                  picker is the only way in, and offering it next to the "no access" alert reads as
                  a working fallback. Scan food (ScanPlate) hides it the same way. */}
              {!camera.isCameraActive && !camera.isSearchingCamera && (
                <Button
                  color="link"
                  size="sm"
                  onClick={() => window.location.reload()}
                  disabled={camera.isSearchingCamera || camera.isCapturing}
                >
                  <p>{t('tracker.scan.button.refreshCamera')}</p>
                </Button>
              )}
            </div>
          ) : (
            <>
              {camera.isTorchSupported ? (
                <button className="scan-plate-button" onClick={camera.toggleTorch}>
                  <img src={camera.isTorchOn ? flashOnIcon : flashOffIcon} alt="" />
                </button>
              ) : (
                <div style={{ width: 40, height: 40 }} />
              )}
              <button
                className="scan-plate-button"
                onClick={handleShutter}
                disabled={camera.isCapturing}
                data-testid="scan-label-shutter"
                data-test-id="scan_label_shutter"
              >
                <img src={cameraShutter} alt="" />
              </button>
              <button className="scan-plate-button" onClick={camera.openGallery}>
                <img src={photoGalleryIcon} alt="" />
              </button>
            </>
          )}
        </div>
      </div>

      {showIntro && (
        // Same bottom sheet as the barcode scanner's tips (Reactstrap Modal + `.select-insert-date`):
        // full-bleed, pinned to the bottom edge, rounded only on top. The bespoke backdrop/sheet pair
        // this replaced sat inset from the sides with all four corners rounded.
        <Modal
          className="from-bottom"
          isOpen={showIntro}
          fullscreen={true}
          size="lg"
          toggle={dismissIntro}
        >
          <div
            className="select-insert-date"
            data-testid="scan-label-intro"
            data-test-id="scan_label_intro"
          >
            <Container>
              <Row>
                <Col xs="12" md={{ size: 4, offset: 4 }}>
                  {/* One shipped illustration instead of two thumbs — it already shows both shots
                      side by side, with the good/bad marks and the label copy baked in (hence
                      per-language). The numbered captions sit below it, one per half, as in Figma
                      8838:17597 (label y=162 against a 150px-tall image). */}
                  <img src={tipsImg} alt="" className="scan-label-intro-img" />
                  <div className="scan-label-intro-captions">
                    <p className="description mb-0">
                      {t('tracker.scanner.intro.imageLabel.front')}
                    </p>
                    <p className="description mb-0">
                      {t('tracker.scanner.intro.imageLabel.nutrition')}
                    </p>
                  </div>

                  <h2 className="title-small bold text-center mt-l mb-xs">
                    {t('tracker.scanner.intro.title')}
                  </h2>
                  <p className="text-text-variant-color text-center mb-l">
                    {t('tracker.scanner.intro.description')}
                  </p>

                  <Button
                    color="primary"
                    className="w-100"
                    onClick={dismissIntro}
                    data-testid="scan-label-intro-cta"
                    data-test-id="scan_label_intro_cta"
                  >
                    {t('tracker.scanner.intro.cta')}
                  </Button>
                </Col>
              </Row>
            </Container>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TrackerScanLabelContainer;
